import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Archive, Download, ExternalLink, FileText, Filter, Link as LinkIcon,
  Plus, RefreshCw, Search, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import ModuleTitleRESOURCES from "@/components/resources/ModuleTitleRESOURCES";

const roleLevel = { volunteer: 0, employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "file", label: "Files" },
  { value: "link", label: "Links" },
];

function humanBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ResourcesLanding() {
  const { user, backendUrl } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);
  const isPoAdmin = roleLevel[user?.role || "employee"] >= 3;
  const isVolunteer = user?.role === "volunteer";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/resources", {
        params: { q: q || undefined, category: category || undefined, type: type || undefined },
      });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resources/categories");
        setCategories(data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, type]);

  const filtered = useMemo(() => items, [items]);

  const download = async (r) => {
    try {
      const res = await fetch(`${backendUrl}/api/resources/${r.id}/download`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Unable to fetch");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || "resource";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("We couldn't fetch that file. Please try again.");
    }
  };

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <ModuleTitleRESOURCES />
          <h1 className="text-4xl font-serif text-forest-900 mt-2">
            {isVolunteer ? "Resources For You" : "Shared Resources & Playbooks"}
          </h1>
          <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
            {isVolunteer
              ? "Warm, practical resources chosen for our volunteer teammates — take a look any time."
              : "A shared library of playbooks, forms, guides, and helpful links. People Operations curates this space."}
          </p>
        </div>
        {isPoAdmin && (
          <Link
            to="/resources/admin"
            className="patria-btn-primary self-start"
            data-testid="btn-manage-resources"
          >
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Manage Resources
          </Link>
        )}
      </div>

      <div className="patria-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted"
            strokeWidth={1.5}
          />
          <input
            className="patria-input w-full pl-9"
            placeholder="Search resources by title or description..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="res-search"
          />
        </div>
        <select
          className="patria-input"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="res-category"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="patria-input"
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="res-type"
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && (
        <p className="italic text-charcoal-muted" data-testid="res-loading">
          Loading resources...
        </p>
      )}

      {!loading && filtered.length === 0 && (
        <div className="patria-card p-8 text-center" data-testid="res-empty">
          <FileText className="h-8 w-8 mx-auto text-charcoal-muted mb-3" strokeWidth={1.5} />
          <p className="font-serif text-xl text-forest-900">Nothing here yet.</p>
          <p className="text-sm text-charcoal-soft mt-1">
            {isVolunteer
              ? "When People Operations shares volunteer resources, they will show up here."
              : "Try a different search or filter. Or ask People Operations to add a resource."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="res-grid">
        {filtered.map((r) => (
          <article
            key={r.id}
            className="patria-card p-5 flex flex-col gap-3"
            data-testid={`res-card-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                r.type === "link" ? "bg-terracotta-500/15 text-terracotta-600" : "bg-forest-900/10 text-forest-900"
              }`}>
                {r.type === "link"
                  ? <LinkIcon className="h-5 w-5" strokeWidth={1.5} />
                  : <FileText className="h-5 w-5" strokeWidth={1.5} />}
              </div>
              <span className="patria-chip-green text-[10px]">{r.category}</span>
            </div>
            <div>
              <h3 className="font-serif text-xl text-forest-900" data-testid={`res-title-${r.id}`}>
                {r.title}
              </h3>
              <p className="mt-1 text-sm text-charcoal-soft line-clamp-4">{r.description}</p>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-charcoal-muted flex flex-wrap gap-2">
              {r.type === "file" ? (
                <span>{r.mime_type?.split("/").pop() || "file"} · {humanBytes(r.size_bytes)}</span>
              ) : (
                <span>External Link</span>
              )}
              {r.visible_to_volunteers && (
                <span className="patria-chip-terra normal-case tracking-normal">
                  <ShieldCheck className="h-3 w-3" /> Visible To Volunteers
                </span>
              )}
            </div>
            <div className="mt-auto pt-2">
              {r.type === "file" ? (
                <button
                  onClick={() => download(r)}
                  className="patria-btn-secondary w-full"
                  data-testid={`res-download-${r.id}`}
                >
                  <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Download
                </button>
              ) : (
                <a
                  href={r.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="patria-btn-secondary w-full"
                  data-testid={`res-open-${r.id}`}
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Open Link
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
